"use client";

/**
 * Persistent top bar: wordmark (home), key status chip with its management
 * popover, theme toggle, locale switcher. The key moved out of the main
 * flow — the staged screen (components/flow/home-flow.tsx) collects it in
 * context when a file is waiting; this chip is where it is inspected,
 * replaced, or removed afterwards.
 */
import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { LocaleSwitcher } from "@/components/locale-switcher";
import { OpenAIKeyForm } from "@/components/settings/openai-key-form";
import { ThemeToggle } from "@/components/theme/theme-toggle";
import {
  clearOpenAIKey,
  getOpenAIBaseUrl,
  DEFAULT_OPENAI_BASE_URL,
  useOpenAIKeyHint,
} from "@/lib/client/openai-key";
import { resetSession, useSessionState } from "@/lib/client/session-store";
import { toast } from "@/lib/toast";
import { cn } from "@/lib/utils";

export function AppHeader() {
  const t = useTranslations("Flow");
  const keyHint = useOpenAIKeyHint();
  const session = useSessionState();
  const [menuOpen, setMenuOpen] = useState(false);
  const [editingKey, setEditingKey] = useState(false);

  useEffect(() => {
    if (!menuOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [menuOpen]);

  function closeMenu() {
    setMenuOpen(false);
    setEditingKey(false);
  }

  function handleGoHome() {
    if (session.flow === "idle") return;
    if (
      session.hasUnexportedContent &&
      !window.confirm(t("workspace.discardConfirm"))
    ) {
      return;
    }
    resetSession();
  }

  const showForm = keyHint === null || editingKey;
  const baseUrl = getOpenAIBaseUrl();

  return (
    <header className="flex h-16 flex-none items-center justify-between gap-4 border-b px-4 sm:px-7">
      <button
        type="button"
        onClick={handleGoHome}
        className="flex cursor-pointer items-center gap-[11px] text-foreground"
      >
        <span className="flex h-[34px] w-[34px] -rotate-[4deg] items-center justify-center rounded-[7px] bg-primary font-serif text-[19px] font-semibold text-primary-foreground shadow-elev">
          文
        </span>
        <span className="font-serif text-[22px] font-medium tracking-[-0.3px]">
          wendai
        </span>
      </button>

      <div className="flex items-center gap-2.5">
        <div className="relative">
          <button
            type="button"
            aria-expanded={menuOpen}
            onClick={() => {
              setMenuOpen((open) => !open);
              setEditingKey(false);
            }}
            className={cn(
              "flex h-8 items-center gap-2 whitespace-nowrap rounded-full border border-input px-3 font-mono text-xs transition-colors hover:bg-accent",
              keyHint === null ? "text-muted-foreground" : "text-foreground",
            )}
          >
            <span
              className={cn(
                "h-[7px] w-[7px] flex-none rounded-full",
                keyHint === null
                  ? "border-[1.5px] border-input"
                  : "bg-primary",
              )}
            />
            {keyHint ?? t("key.chipNoKey")}
          </button>

          {menuOpen && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={closeMenu}
                aria-hidden
              />
              <div className="absolute right-0 top-[calc(100%+8px)] z-50 w-[308px] animate-[wd-rise_.18s_ease_both] rounded-xl border bg-popover p-[18px] text-left shadow-elev">
                <p className="mb-0.5 text-[13px] font-semibold">
                  {t("key.statusTitle")}
                </p>
                {showForm ? (
                  <>
                    <p className="mb-2.5 text-xs text-muted-foreground">
                      {t("key.setupDescription")}
                    </p>
                    <OpenAIKeyForm
                      submitLabel={t("key.save")}
                      autoFocus
                      onSaved={() => {
                        closeMenu();
                        toast.success(t("key.connected"));
                      }}
                    />
                  </>
                ) : (
                  <>
                    <p className="mb-2.5 text-xs text-muted-foreground">
                      {t("key.statusDescription")}
                    </p>
                    <p className="mb-1 break-all font-mono text-[13px]">
                      {keyHint}
                    </p>
                    {baseUrl !== DEFAULT_OPENAI_BASE_URL && (
                      <p className="mb-1 break-all text-xs text-muted-foreground">
                        {baseUrl}
                      </p>
                    )}
                    <div className="mt-3 flex gap-2">
                      <button
                        type="button"
                        onClick={() => setEditingKey(true)}
                        className="h-8 rounded-lg border border-input px-3 text-[12.5px] font-medium transition-colors hover:bg-accent"
                      >
                        {t("key.replace")}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          clearOpenAIKey();
                          setMenuOpen(false);
                        }}
                        className="h-8 rounded-lg px-3 text-[12.5px] font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                      >
                        {t("key.remove")}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>

        <ThemeToggle />
        <LocaleSwitcher />
      </div>
    </header>
  );
}
